import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { getFlavor, type Flavor } from './data'
import FlavorGrid from './components/FlavorGrid'
import FlavorDetail from './components/FlavorDetail'
import './FlavorsPage.css'

/**
 * «Вкусовой конструктор» — раздел на основе графа сочетаний из книги «Тезаурус вкусов».
 * Два экрана в одной странице, состояние — в URL (?flavor=), чтобы работали
 * кнопка «Назад» и перезагрузка:
 *   1) сетка вкусов по категориям;
 *   2) экран вкуса: колесо сочетаний с уровнями смелости (пара — конечная точка,
 *      отдельного экрана пары нет).
 */
const FlavorsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')

  const flavorSlug = searchParams.get('flavor') ?? ''

  // Валидируем slug из URL: неизвестный вкус → возврат к сетке.
  const flavor = useMemo<Flavor | undefined>(
    () => (flavorSlug ? getFlavor(flavorSlug) : undefined),
    [flavorSlug],
  )

  const openFlavor = useCallback(
    (f: Flavor) => setSearchParams({ flavor: f.slug }),
    [setSearchParams],
  )

  const backToGrid = useCallback(
    () => setSearchParams({}),
    [setSearchParams],
  )

  // Экран вкуса
  if (flavor) {
    return (
      <div className="flavors-page">
        <FlavorDetail flavor={flavor} onBack={backToGrid} />
      </div>
    )
  }

  // Сетка вкусов
  return (
    <div className="flavors-page">
      <header className="flavors-header">
        <h1 className="flavors-title">Вкусовой конструктор</h1>
        <p className="flavors-lead">
          Выберите вкус — и посмотрите, с чем он сочетается. По книге
          «Тезаурус вкусов» Ники Сегнит.
        </p>
      </header>

      <div className="flavors-search">
        <Icon
          icon="solar:magnifer-linear"
          width={20}
          height={20}
          className="flavors-search__icon"
        />
        <input
          type="text"
          className="flavors-search__input"
          placeholder="Найти вкус"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button
            type="button"
            className="flavors-search__clear"
            onClick={() => setSearch('')}
            aria-label="Очистить"
          >
            <Icon icon="solar:close-circle-linear" width={20} height={20} />
          </button>
        )}
      </div>

      <FlavorGrid search={search} onSelect={openFlavor} />
    </div>
  )
}

export default FlavorsPage
